import { useState, useEffect, useRef, useCallback } from 'react';
import { CmsSidebar } from '../../modules/cms/layouts/CmsSidebar';
import { SidebarFlyout } from './SidebarFlyout';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { notificationsService } from '@/services/notificationsService';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';

const SIDEBAR_SCROLL_KEY = 'kibo_sidebar_scroll_pos';

function getSavedSidebarScroll(): number {
  try {
    const v = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    return v ? Number(v) : 0;
  } catch {
    return 0;
  }
}

function saveSidebarScroll(pos: number): void {
  try {
    sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(pos));
  } catch {
    // ignore
  }
}

import {
  LayoutDashboard, Server, AlertTriangle,
  Users, Building2, Shield,
  ChevronRight, LogOut, HelpCircle, ClipboardList, ChevronLeft,
  PanelLeftClose, PanelLeftOpen,
  Mail, CirclePlus, BookUser,
  ShieldAlert, Home, Radar, RadioTower, ShieldCheck, BarChart2,
  FileText, KeyRound,
  BadgeCheck, Layers, Share2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/config/routes';
import { useAuthStore } from '@/store/authStore';
import { filterNav } from './navFilter';
import { AppLogo } from './AppLogo';
import { ICON_ACTIVE, ICON_DEFAULT, NAV_DEFAULT, NAV_ROW, NAV_SUB_LIST, NAV_SUB_ROW, navItemState } from './navStyles';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  /**
   * Hides this link unless the user holds the permission. Sub-items used to be
   * rendered unfiltered, so any visible section exposed every link beneath it.
   * Omitting the field keeps a link visible to everyone who can see the
   * section, which is right for the ones that are only a view of data the
   * section already implies.
   */
  requiredPermission?: string;
  submenu?: Array<{
    name: string;
    href: string;
    icon: React.ElementType;
    requiredPermission?: string;
  }>;
}

// 'cms' matches no entry in `teams` on purpose: the CMS module renders its own
// sidebar (CmsSidebar) with its own highlight, so while a /cms route is open
// nothing in the main nav should look selected.
type TeamId = 'home' | 'directory' | 'assets' | 'incident' | 'threat_intel' | 'messages' | 'analytics' | 'dashboards' | 'organizations' | 'users' | 'api_keys' | 'org_profile' | 'team' | 'audit' | 'services' | 'roles' | 'cms';

const amNavItems: NavItem[] = [
  { name: 'Dashboard', href: ROUTES.DASHBOARD, icon: LayoutDashboard },
  { name: 'Assets', href: ROUTES.ASSETS, icon: Server, requiredPermission: 'assets:read' },
  {
    name: 'Vulnerabilities',
    href: ROUTES.VULNERABILITIES,
    icon: AlertTriangle,
    requiredPermission: 'vulnerabilities:read',
  },
];

// The section dashboards below are gated too. They are not neutral landing
// pages: each one renders the section's own data, so a user without the read
// permission reaches Unauthorized. Leaving them unguarded also kept the whole
// section in the sidebar, since a section survives on its remaining links.
const orgIncidentNavItems: NavItem[] = [
  {
    name: 'Dashboard',
    href: ROUTES.NIRP_ORG_DASHBOARD,
    icon: LayoutDashboard,
    requiredPermission: 'incidents:read',
  },
  {
    name: 'Submit Incident',
    href: ROUTES.NIRP_SUBMIT_INCIDENT,
    icon: CirclePlus,
    requiredPermission: 'incidents:create',
  },
  {
    name: 'My Incidents',
    href: ROUTES.NIRP_MY_INCIDENTS,
    icon: ClipboardList,
    requiredPermission: 'incidents:read',
  },
];

const adminIncidentNavItems: NavItem[] = [
  {
    name: 'Admin Dashboard',
    href: ROUTES.NIRP_ADMIN_DASHBOARD,
    icon: LayoutDashboard,
    requiredPermission: 'incidents:read',
  },
  {
    name: 'All Incidents',
    href: ROUTES.NIRP_ADMIN_INCIDENTS,
    icon: ClipboardList,
    requiredPermission: 'incidents:read',
  },
];

const adminThreatIntelNavItems: NavItem[] = [
  {
    name: 'Manage Content',
    href: ROUTES.NIRP_ADMIN_THREAT_INTEL,
    icon: ShieldCheck,
    requiredPermission: 'threat-intel:create',
  },
  {
    name: 'Threat Sharing',
    href: ROUTES.NIRP_ADMIN_THREAT_SHARE_QUEUE,
    icon: Share2,
    requiredPermission: 'authored-threat-shares:review',
  },
];

// org_admin (non-platform) gets the sharing tools alongside the org feed
const orgAdminThreatIntelNavItems: NavItem[] = [
  {
    name: 'Dashboard & Statistics',
    href: ROUTES.NIRP_THREAT_DASHBOARD,
    icon: LayoutDashboard,
    requiredPermission: 'threat-intel:read',
  },
  {
    name: 'Shared Feed',
    href: ROUTES.NIRP_THREAT_FEED,
    icon: RadioTower,
    requiredPermission: 'threat-intel:read',
  },
  {
    name: 'Threat Sharing',
    href: ROUTES.NIRP_ADMIN_THREAT_SHARE_QUEUE,
    icon: Share2,
    requiredPermission: 'authored-threat-shares:create',
  },
];

const servicesNavItems: NavItem[] = [
  { name: 'Overview', href: ROUTES.ADMIN_SERVICES, icon: LayoutDashboard },
  { name: 'Sold Services', href: ROUTES.ADMIN_SERVICES_SOLD, icon: Layers },
];

const orgThreatIntelNavItems: NavItem[] = [
  {
    name: 'Dashboard & Statistics',
    href: ROUTES.NIRP_THREAT_DASHBOARD,
    icon: LayoutDashboard,
    requiredPermission: 'threat-intel:read',
  },
  {
    name: 'Shared Feed',
    href: ROUTES.NIRP_THREAT_FEED,
    icon: RadioTower,
    requiredPermission: 'threat-intel:read',
  },
];

interface SidebarProps {
  /**
   * Rendered inside the mobile navigation drawer. The collapse toggle (which
   * only makes sense beside the desktop content) is hidden, and the sidebar
   * always shows expanded — the drawer manages its own width.
   */
  inDrawer?: boolean;
  /**
   * Owned by MainLayout, not local state — the toggle control lives in the
   * Header (beside the page title) and, now, the sidebar logo itself, so all
   * three need the same value. Ignored when inDrawer, which always renders
   * expanded regardless.
   */
  collapsed?: boolean;
  /**
   * Pins/unpins the collapsed state. Wired to the logo click / toggle arrow. Omitted (and
   * so a no-op) inside the mobile drawer, which has no collapse concept.
   */
  onToggleCollapsed?: () => void;
  onSetCollapsed?: (collapsed: boolean) => void;
}

export function Sidebar({ inDrawer = false, collapsed = false, onToggleCollapsed, onSetCollapsed }: SidebarProps = {}) {
  const [activeTeam, setActiveTeam] = useState<TeamId>('home');
  const [openModule, setOpenModule] = useState<TeamId>('home');
  const { logout, hasRole, hasPermission } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const navScrollRef = useRef<HTMLDivElement>(null);

  const showExpanded = inDrawer || !collapsed;

  // ── Drag-to-resize & drag-to-collapse / expand ────────────────────────────
  const DEFAULT_WIDTH = 256;
  const MIN_WIDTH = 200;
  const MAX_WIDTH = 450;
  const COLLAPSE_THRESHOLD = 140;

  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('kibo_sidebar_width');
      if (saved) {
        const parsed = Number(saved);
        if (parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) return parsed;
      }
    } catch {}
    return DEFAULT_WIDTH;
  });

  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (inDrawer) return;
    e.preventDefault();
    setIsDragging(true);
  }, [inDrawer]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const mouseX = e.clientX;
      if (mouseX < COLLAPSE_THRESHOLD) {
        if (!collapsed) {
          if (onSetCollapsed) onSetCollapsed(true);
          else onToggleCollapsed?.();
        }
      } else {
        const clampedWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, mouseX));
        setSidebarWidth(clampedWidth);
        try {
          localStorage.setItem('kibo_sidebar_width', String(clampedWidth));
        } catch {}
        if (collapsed) {
          if (onSetCollapsed) onSetCollapsed(false);
          else onToggleCollapsed?.();
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, collapsed, onSetCollapsed, onToggleCollapsed]);

  // Badge for the "Notification" item — same unread-count source as the
  // Header bell and the CMS section's own Notifications sub-item, so all
  // three stay in sync.
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const loadUnreadNotifCount = useCallback(() => {
    notificationsService
      .count()
      .then((res) => setUnreadNotifCount(res.unreadCount ?? 0))
      .catch(() => {});
  }, []);
  useEffect(() => {
    loadUnreadNotifCount();
  }, [loadUnreadNotifCount]);
  useNotificationSocket({
    onUnreadCount: (count) => setUnreadNotifCount(count),
    onConnect: () => loadUnreadNotifCount(),
  });

  // Restore scroll position on mount so navigation does not reset to top
  useEffect(() => {
    const saved = getSavedSidebarScroll();
    if (saved && navScrollRef.current) {
      navScrollRef.current.scrollTop = saved;
    }
  }, []);

  const handleNavScroll = (e: React.UIEvent<HTMLDivElement>) => {
    saveSidebarScroll(e.currentTarget.scrollTop);
  };

  const isPlatformAdmin = hasRole('platform_admin');
  const isOrgAdmin = hasRole('org_admin') || hasRole('manager');
  // Asset sub-items are shown by permission alone, via each item's
  // requiredPermission. Installing the agent is no longer a sidebar item: it
  // is one of the two ways to add an asset, offered from the Assets page's
  // "Add asset" menu (ROUTES.ENROLL_ASSET).
  const amNavItemsForCurrentUser = amNavItems;

  const checkSubActive = (itemHref?: string, teamItems?: NavItem[]) => {
    if (!itemHref) return false;
    return itemHref === ROUTES.DASHBOARD ||
      itemHref === ROUTES.NIRP_ORG_DASHBOARD ||
      itemHref === ROUTES.NIRP_ADMIN_DASHBOARD ||
      itemHref === ROUTES.HOME ||
      itemHref === ROUTES.ADMIN_SERVICES
      ? location.pathname === itemHref
      : location.pathname === itemHref ||
        (location.pathname.startsWith(`${itemHref}/`) &&
          !(teamItems || []).some(
            (sibling) =>
              sibling.href !== itemHref &&
              sibling.href?.startsWith(`${itemHref}/`) &&
              (location.pathname === sibling.href ||
                location.pathname.startsWith(`${sibling.href}/`)),
          ));
  };

  // Automatically switch team based on current route
  useEffect(() => {
    if (location.pathname === ROUTES.HOME || location.pathname === '/home') {
      // Everyone lands on the home dashboard now, so Home is the active tab for
      // every role — not just platform admins, who used to be the only ones
      // with a Home item.
      setActiveTeam('home');
    } else if (location.pathname.startsWith(ROUTES.DIRECTORY)) {
      setActiveTeam('directory');
    } else if (
      location.pathname.startsWith(ROUTES.NIRP_ADMIN_THREAT_INTEL)
      || location.pathname.startsWith(ROUTES.NIRP_THREAT_FEED)
      || location.pathname.startsWith(ROUTES.NIRP_THREAT_DASHBOARD)
      || location.pathname.startsWith(ROUTES.NIRP_ADMIN_THREAT_SHARE_QUEUE)
    ) {
      setActiveTeam('threat_intel');
    } else if (location.pathname.startsWith(ROUTES.NIRP_MESSAGES)) {
      setActiveTeam('messages');
    } else if (location.pathname === ROUTES.ANALYTICS) {
      setActiveTeam('analytics');
    } else if (location.pathname.startsWith(ROUTES.DASHBOARD_BUILDER)) {
      setActiveTeam('dashboards');
    } else if (location.pathname.startsWith(ROUTES.ADMIN_AUDIT_LOGS)) {
      setActiveTeam('audit');
    } else if (location.pathname.startsWith(ROUTES.ADMIN_EXTERNAL_API_KEYS)) {
      setActiveTeam('api_keys');
    } else if (location.pathname.startsWith(ROUTES.ADMIN_SERVICES)) {
      setActiveTeam('services');
    } else if (location.pathname.startsWith(ROUTES.ADMIN_ORGANIZATIONS)) {
      setActiveTeam('organizations');
    } else if (location.pathname.startsWith(ROUTES.ADMIN_ROLES)) {
      setActiveTeam('roles');
    } else if (location.pathname.startsWith(ROUTES.ADMIN_USERS)) {
      setActiveTeam('users');
    } else if (location.pathname === ROUTES.NIRP_PROFILE) {
      setActiveTeam('org_profile');
    } else if (location.pathname.startsWith(ROUTES.TEAM)) {
      setActiveTeam('team');
    } else if (location.pathname.startsWith('/cms')) {
      // CMS owns its own sidebar and highlight, so the main nav shows nothing
      // selected. This has to be an id no team uses — pointing it at 'directory'
      // as a stand-in lit Directory up on every CMS page.
      setActiveTeam('cms');
    } else if (location.pathname.startsWith('/nirp')) {
      setActiveTeam('incident');
    } else {
      setActiveTeam('assets');
    }
  }, [location.pathname]);

  useEffect(() => {
    setOpenModule(activeTeam);
  }, [activeTeam]);

  // Which sections exist, and the permission each one needs to be visible.
  //
  // `requiredPermission` decides visibility; roles still decide which *variant*
  // of a section to show, because "platform admins see every org's incidents"
  // is a scope question rather than a permission one. Omitting the field means
  // any signed-in user sees the section.
  const allTeams: Array<{
    id: string;
    name: string;
    icon: typeof Home;
    items: NavItem[];
    href: string;
    requiredPermission?: string;
    /** Visible if the user holds ANY of these. Use for sections that host
     *  several independently-gated pages, where demanding one specific
     *  permission would hide the others. */
    requiredAnyPermission?: string[];
  }> = [
    // Every role now lands on the home dashboard (HomeDashboard resolves the
    // right view per role), so every role gets a Home entry to represent it.
    // Platform admins keep the "Admin Home" label they had.
    {
      id: 'home',
      name: isPlatformAdmin ? 'Admin Home' : 'Home',
      icon: Home,
      items: [],
      href: ROUTES.HOME,
    },
    {
      id: 'incident',
      name: 'Incident Management',
      icon: ShieldAlert,
      items: isPlatformAdmin ? adminIncidentNavItems : orgIncidentNavItems,
      href: isPlatformAdmin ? ROUTES.NIRP_ADMIN_DASHBOARD : ROUTES.NIRP_ORG_DASHBOARD,
    },
    {
      id: 'threat_intel',
      name: 'Threat Intelligence',
      icon: Radar,
      items: isPlatformAdmin ? adminThreatIntelNavItems : isOrgAdmin ? orgAdminThreatIntelNavItems : orgThreatIntelNavItems,
      href: isPlatformAdmin ? ROUTES.NIRP_ADMIN_THREAT_INTEL : ROUTES.NIRP_THREAT_DASHBOARD,
    },
    {
      id: 'assets',
      name: 'Asset Management',
      icon: Server,
      items: amNavItemsForCurrentUser,
      href: ROUTES.DASHBOARD,
      // Any one of these is enough to open the section, because its links are
      // gated individually below. Requiring `assets:read` alone hid the whole
      // section from a role granted only "view vulnerabilities", taking the
      // Vulnerabilities link down with it even though that page was permitted.
      requiredAnyPermission: [
        'assets:read',
        'vulnerabilities:read',
      ],
    },
    {
      id: 'analytics',
      name: 'Analytics & Reports',
      icon: BarChart2,
      items: [],
      href: ROUTES.ANALYTICS,
      requiredPermission: 'analytics:read',
    },
    { id: 'organizations', name: 'Organizations', icon: Building2, items: [], href: ROUTES.ADMIN_ORGANIZATIONS, requiredPermission: 'platform:orgs-manage' },
    { id: 'services', name: 'Services & Revenue', icon: Layers, items: servicesNavItems, href: ROUTES.ADMIN_SERVICES, requiredPermission: 'platform:orgs-manage' },
    // { id: 'users', name: 'Users', icon: Users, items: [], href: ROUTES.ADMIN_USERS, requiredPermission: 'platform:users-manage' },
    { id: 'api_keys', name: 'API Keys', icon: KeyRound, items: [], href: ROUTES.ADMIN_EXTERNAL_API_KEYS, requiredPermission: 'platform:api-keys-manage' },
    // The org-scoped variants. Visibility is decided by permission
    // (requiredPermission below, enforced by the filter), NOT by holding
    // org_admin — a user granted only "view users" must see the Team section
    // even though they are not an admin. The !isPlatformAdmin guard stays: a
    // platform admin has their own "Users"/"Organizations" sections above, so
    // this only excludes them from the duplicate org-scoped entries.
    ...(!isPlatformAdmin ? [
      // { id: 'org_profile', name: 'Organization Profile', icon: Building2, items: [], href: ROUTES.NIRP_PROFILE, requiredPermission: 'orgs:update' },
      { id: 'team', name: 'Team / Users', icon: Users, items: [], href: ROUTES.TEAM, requiredPermission: 'users:read' },
    ] : []),
    { id: 'roles', name: 'Roles & Permissions', icon: ShieldCheck, items: [], href: ROUTES.ADMIN_ROLES, requiredPermission: 'roles:read' },
    { id: 'audit', name: 'Audit Logs', icon: FileText, items: [], href: ROUTES.ADMIN_AUDIT_LOGS, requiredPermission: 'audit:read' },
    { id: 'directory', name: 'Directory', icon: BookUser, items: [], href: ROUTES.DIRECTORY },
    { id: 'messages', name: 'Notification', icon: Mail, items: [], href: ROUTES.NIRP_MESSAGES },
  ];

  // Sections, then the links inside them. See navFilter.ts for the rule; it
  // lives there so the tests exercise the same code this renders.
  const teams = filterNav(allTeams, hasPermission);

  return (
    <TooltipProvider delayDuration={0}>
      {/* ── Sidebar shell ─────────────────────────────────────────────────── */}
      <aside
        style={{
          width: inDrawer ? undefined : (collapsed ? 80 : sidebarWidth),
        }}
        className={cn(
          // Layout
          'relative z-30 shrink-0 flex flex-col justify-between h-screen',
          'px-3 py-5',
          !isDragging && 'transition-[width] duration-200 ease-out',
          // Theming — uses CSS token classes; responds to light / dark / brand / hc automatically
          'bg-sidebar text-sidebar-foreground',
          'border-r border-sidebar-border',
          'hc:bg-black hc:border-r-2 hc:border-white hc:text-white',
          inDrawer && 'w-full h-full',
        )}
      >
        {/* ── Top Header: Centered Logo & Collapse Toggle Button ───────────── */}
        <div className="w-full shrink-0 mb-3">
          <div className="relative flex items-center justify-center w-full min-h-[64px]">
            {/* Logo */}
            {collapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onToggleCollapsed?.()}
                    title="Expand sidebar"
                    aria-label="Expand sidebar"
                    className="flex h-10 w-10 items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-border rounded-lg p-1"
                  >
                    <AppLogo
                      variant="icon"
                      alt="BlueB"
                      className="h-8 w-8 object-contain"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-semibold">
                  Expand sidebar
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to={ROUTES.HOME}
                className="flex items-center justify-center transition-transform hover:opacity-90 px-1"
              >
                <AppLogo
                  variant="full"
                  alt="BlueB"
                  className="h-[54px] w-auto max-w-[195px]"
                />
              </Link>
            )}

            {/* Collapse Toggle Button - visible ONLY when expanded, hidden when collapsed */}
            {showExpanded && onToggleCollapsed && (
              <button
                type="button"
                onClick={() => onToggleCollapsed()}
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
                className="absolute right-0 top-1/2 -translate-y-1/2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-border cursor-pointer"
              >
                <PanelLeftClose className="h-4 w-4" strokeWidth={2} />
              </button>
            )}
          </div>

          {showExpanded && (
            <div className="mt-3 w-full border-t border-sidebar-border hc:border-white/70" />
          )}
        </div>

        {/* ── Scrollable nav area ─────────────────────────────────────────── */}
        <div
          ref={navScrollRef}
          onScroll={handleNavScroll}
          className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto overflow-x-visible pb-3 hide-scrollbar"
        >
          {/* ── Module list ─────────────────────────────────────────────── */}
          <div className="w-full">
            {!showExpanded ? (
              /* Collapsed: icon-only buttons with hover flyouts for sub-menus */
              <div className="flex flex-col items-center gap-2">
                {teams.map((team) => {
                  const hasSubmenu = team.items.length > 0;
                  const isTeamActive = activeTeam === team.id;

                  if (hasSubmenu) {
                    const flyoutItems = team.items.map((sub) => ({
                      name: sub.name,
                      href: sub.href || '#',
                      icon: sub.icon,
                      isActive: checkSubActive(sub.href, team.items),
                    }));

                    return (
                      <SidebarFlyout
                        key={team.id}
                        title={team.name}
                        icon={team.icon}
                        isActive={isTeamActive}
                        items={flyoutItems}
                        onTriggerClick={() => {
                          setActiveTeam(team.id as TeamId);
                          if (team.href) navigate(team.href);
                        }}
                        triggerClassName={cn(
                          'relative',
                          isTeamActive ? ICON_ACTIVE : ICON_DEFAULT,
                        )}
                        triggerBadge={
                          team.id === 'messages' && unreadNotifCount > 0 ? (
                            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white leading-none">
                              {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                            </span>
                          ) : undefined
                        }
                      />
                    );
                  }

                  return (
                    <Tooltip key={team.id} delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => {
                            setActiveTeam(team.id as TeamId);
                            if (team.href) navigate(team.href);
                          }}
                          className={cn(
                            'relative',
                            isTeamActive ? ICON_ACTIVE : ICON_DEFAULT,
                          )}
                        >
                          <team.icon className="h-5 w-5 text-primary" strokeWidth={2} />
                          {team.id === 'messages' && unreadNotifCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white leading-none">
                              {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                            </span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="font-semibold">
                        {team.name}
                        {team.id === 'messages' && unreadNotifCount > 0 && (
                          <span className="ml-1.5 text-rose-400">({unreadNotifCount} unread)</span>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            ) : (
              /* Expanded: labelled rows with optional sub-menu */
              <div className="relative">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-y-1.5">
                    {teams.map((team) => (
                      <div key={team.id} className="flex flex-col gap-y-1.5">
                        <button
                          onClick={() => {
                            const hasItems = team.items.length > 0;
                            setActiveTeam(team.id as TeamId);
                            if (hasItems) {
                              setOpenModule((prev) =>
                                prev === team.id ? 'home' : (team.id as TeamId),
                              );
                            }
                            if (team.href) navigate(team.href);
                          }}
                          className={cn(NAV_ROW, navItemState(activeTeam === team.id))}
                          title={team.name}
                        >
                          <team.icon
                            className="h-5 w-5 shrink-0 text-primary transition-colors"
                            strokeWidth={activeTeam === team.id ? 2.4 : 2}
                          />
                          <span className="min-w-0 truncate">{team.name}</span>
                          {team.id === 'messages' && unreadNotifCount > 0 && (
                            <span className={cn(
                              'shrink-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-black text-white px-1.5 leading-none',
                              team.items.length === 0 && 'ml-auto',
                            )}>
                              {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                            </span>
                          )}
                          {team.items.length > 0 && (
                            <ChevronRight
                              className={cn(
                                'ml-auto h-4 w-4 transition-transform duration-150',
                                openModule === team.id && 'rotate-90',
                              )}
                              strokeWidth={2}
                            />
                          )}
                        </button>

                        {/* Sub-menu */}
                        {team.items.length > 0 && openModule === team.id && (
                          <div className={NAV_SUB_LIST}>
                            {team.items.map((item) => {
                              const Icon = item.icon;
                              const isActive = checkSubActive(item.href, team.items);

                              return (
                                <Link
                                  key={item.href || item.name}
                                  to={item.href || '#'}
                                  className={cn(NAV_SUB_ROW, navItemState(isActive))}
                                  title={item.name}
                                >
                                  <Icon className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
                                  <span className="min-w-0 truncate">{item.name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <CmsSidebar collapsed={!showExpanded} />
        </div>


        {/* ── Bottom section ───────────────────────────────────────────────── */}
        <div className={cn(
          'flex flex-col gap-y-1.5 border-t border-sidebar-border pt-4',
          'hc:border-white',
          !showExpanded && 'items-center',
        )}>

          {/* Support Center */}
          {!showExpanded ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Link to={ROUTES.SUPPORT} className={ICON_DEFAULT}>
                  <HelpCircle className="h-4 w-4" strokeWidth={2} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-semibold">Support Center</TooltipContent>
            </Tooltip>
          ) : (
            <Link
              to={ROUTES.SUPPORT}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm border-l-[3px] border-transparent',
                NAV_DEFAULT,
              )}
            >
              <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
              <span>Support Center</span>
            </Link>
          )}

          {/* Support Admin — platform admins only */}
          {isPlatformAdmin && (
            !showExpanded ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Link to={ROUTES.SUPPORT_ADMIN} className={ICON_DEFAULT}>
                    <Shield className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-semibold">Support Admin</TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to={ROUTES.SUPPORT_ADMIN}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm border-l-[3px] border-transparent',
                  NAV_DEFAULT,
                )}
              >
                <Shield className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span>Support Admin</span>
              </Link>
            )
          )}

          {/* Sign Out */}
          {!showExpanded ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={logout}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-md',
                    'text-red-500/80 hover:text-red-600',
                    'dark:text-red-400/80 dark:hover:text-red-400',
                    'transition-colors duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400',
                    'focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
                    'hc:text-white hc:hover:bg-white/10',
                  )}
                >
                  <LogOut className="h-4 w-4" strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-semibold text-destructive">Sign Out</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={logout}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm border-l-[3px] border-transparent',
                'text-red-500/80 hover:text-red-600',
                'dark:text-red-400/80 dark:hover:text-red-400',
                'transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400',
                'focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
                'hc:text-white hc:hover:bg-white/10',
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" strokeWidth={2} />
              <span className="font-medium">Sign Out</span>
            </button>
          )}
        </div>

        {/* ── Draggable resize handle on right edge ─────────────────────── */}
        {!inDrawer && (
          <div
            onMouseDown={handleMouseDown}
            onDoubleClick={() => onToggleCollapsed?.()}
            title={collapsed ? 'Drag right to expand, or double-click' : 'Drag left to collapse, or resize width'}
            className={cn(
              'absolute top-0 -right-1.5 bottom-0 w-3 cursor-col-resize z-50 select-none group',
              'flex items-center justify-center'
            )}
          >
            <div
              className={cn(
                'h-full transition-all duration-150',
                isDragging
                  ? 'bg-primary w-1 opacity-100 shadow-[0_0_8px_rgba(59,130,246,0.6)]'
                  : 'w-[2px] bg-transparent group-hover:bg-primary/60'
              )}
            />
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}
